// Kill feed (§11.5, §11.6, §14.3): `[glyph] Grumbo  BONKED  [glyph] Pudding`, sliding in from the
// right, holding 4 s, then fading; at most 5 lines (3 in compact mode). The verb comes from the
// KO's cause; self-falls read SELF-YEETED. Ghost credits get a small ghost icon; REVENGE, BOUNTY,
// DOUBLE BONK and teammate KOs add tags. Lines involving the local bean are highlighted.
import { NO_SLOT } from '@shared/constants';
import { KoFlag, type SimEvent } from '@shared/events';
import type { BeanView } from '../../game/FrameView';
import { icon, identityBadge } from '../icons';
import { killVerb } from '../labels';
import { el } from '../ui';

export const FEED_HOLD_MS = 4000;
const FEED_FADE_MS = 450;
export const FEED_LINES = 5;
export const FEED_LINES_COMPACT = 3;

/** What one feed line says, independent of the DOM (unit-tested). */
export interface FeedLine {
  victim: number;
  killer: number;
  verb: string;
  self: boolean;
  ghostKiller: boolean;
  tags: ('REVENGE' | 'BOUNTY' | 'DOUBLE BONK' | 'TEAMMATE!')[];
}

/** Builds the feed line for a KO event (§11.6). */
export function feedLine(ev: SimEvent): FeedLine {
  const self = (ev.d & KoFlag.SELF) !== 0 || ev.b === NO_SLOT || ev.b === ev.a;
  const tags: FeedLine['tags'] = [];
  if (!self && (ev.d & KoFlag.REVENGE) !== 0) tags.push('REVENGE');
  if (!self && (ev.d & KoFlag.BOUNTY) !== 0) tags.push('BOUNTY');
  if ((ev.d & KoFlag.DOUBLE) !== 0) tags.push('DOUBLE BONK');
  if (!self && (ev.d & KoFlag.TEAMKILL) !== 0) tags.push('TEAMMATE!');
  return { victim: ev.a, killer: self ? NO_SLOT : ev.b, verb: killVerb(ev.c, self), self, ghostKiller: !self && (ev.d & KoFlag.GHOST_KO) !== 0, tags };
}

export interface KillFeed {
  readonly el: HTMLElement;
  push(ev: SimEvent, beans: readonly BeanView[], localSlot: number, colorblind: boolean): void;
  clear(): void;
  update(nowMs: number, compact: boolean): void;
}

interface Line { node: HTMLElement; at: number; leaving: boolean }

function who(b: BeanView | undefined, slot: number, colorblind: boolean, ghost: boolean): HTMLElement {
  const name = b && b.name ? b.name : `Bean ${slot + 1}`;
  return el('span', { class: 'kf-who' },
    ghost ? el('span', { class: 'kf-ghost' }, icon('ghost')) : null,
    identityBadge(b?.identity ?? slot, colorblind, 'idbadge sm'),
    el('b', { class: 'kf-name', text: name }));
}

export function createKillFeed(): KillFeed {
  const root = el('div', { class: 'killfeed', attrs: { 'aria-live': 'polite', 'aria-label': 'Kill feed' } });
  const lines: Line[] = [];

  function drop(line: Line): void {
    if (line.leaving) return;
    line.leaving = true;
    line.node.classList.add('out');
    setTimeout(() => line.node.remove(), FEED_FADE_MS);
  }

  return {
    el: root,
    push(ev, beans, localSlot, colorblind) {
      const f = feedLine(ev);
      const mine = f.killer === localSlot && localSlot !== NO_SLOT;
      const victimMe = f.victim === localSlot && localSlot !== NO_SLOT;
      const node = el('div', { class: `kf-line${mine ? ' mine' : ''}${victimMe ? ' me-out' : ''}${f.self ? ' self' : ''}` },
        f.self ? who(beans[f.victim], f.victim, colorblind, false) : who(beans[f.killer], f.killer, colorblind, f.ghostKiller),
        el('span', { class: `kf-verb${f.self ? ' oops' : ''}`, text: f.verb }),
        f.self ? null : who(beans[f.victim], f.victim, colorblind, false),
        ...f.tags.map((t) => el('span', { class: `kf-tag ${t === 'REVENGE' ? 'revenge' : t === 'BOUNTY' ? 'bounty' : 'misc'}`, text: t })));
      root.prepend(node);
      lines.unshift({ node, at: performance.now(), leaving: false });
      while (lines.length > FEED_LINES) {
        const old = lines.pop();
        if (old) drop(old);
      }
    },
    clear() {
      for (const l of lines) l.node.remove();
      lines.length = 0;
    },
    update(now, compact) {
      const max = compact ? FEED_LINES_COMPACT : FEED_LINES;
      for (let i = lines.length - 1; i >= 0; i--) {
        const l = lines[i];
        if (now - l.at > FEED_HOLD_MS || i >= max) {
          drop(l);
          lines.splice(i, 1);
        }
      }
    },
  };
}
